/**
 * Permission codes the Cash & Bank screens declare an interest in.
 *
 * They are the dot-strings `internal/cash/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once
 * here means a renamed permission is a compile error rather than a menu that
 * silently disappears.
 *
 * `cash.account.reveal` is registered `RequiresMFA`, which is what makes the
 * server put a step-up guard in front of the route. The reveal dialog therefore
 * treats `ERR_STEP_UP_REQUIRED` as a guided step rather than a refusal — see
 * `RevealNumberDialog.vue`.
 */

export const CASH_PERMISSIONS = {
  accountRead: 'cash.account.read',
  accountCreate: 'cash.account.create',
  accountUpdate: 'cash.account.update',
  accountReveal: 'cash.account.reveal',

  /*
   * The bank account lifecycle (FSD TRS-S01, FT1-B1). Each one is a separate
   * principal's act: the server refuses the recorder as verifier, the recorder or
   * submitter as activator, the maker as amendment checker and the requester as
   * close authoriser (ERR_SOD_VIOLATION). These codes decide only what a screen
   * offers; the server stays the authority.
   */
  accountVerify: 'cash.account.verify',
  accountActivate: 'cash.account.activate',
  accountAmend: 'cash.account.amend',
  accountAmendApprove: 'cash.account.amend_approve',
  accountSuspend: 'cash.account.suspend',
  accountClose: 'cash.account.close',
  accountCloseApprove: 'cash.account.close_approve',
  /** The bank register (`cash_banks`) the account's bank is picked from. */
  bankManage: 'cash.bank.manage',

  /*
   * FT4 (R030/R047/R062): the v1 transfer, position and forecast screens are
   * gone. Transfer instructions declare their codes in
   * `cash/transfers/permissions.ts`; positions and forecast runs are treasury's
   * (`treasury.position.read`, `treasury.forecast.read`).
   */

  recurringRead: 'cash.recurring.read',
  recurringManage: 'cash.recurring.manage',

  /** Read companies to scope every screen in this module. */
  companyRead: 'org.company.read',
  /** Read the chart of accounts for the ledger-mapping and in-transit pickers. */
  accountsRead: 'org.account.read',
  /** Drill from a cash balance into the ledger behind it. */
  glReportRead: 'gl.report.read',
} as const

export type CashPermission = (typeof CASH_PERMISSIONS)[keyof typeof CASH_PERMISSIONS]
